
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { google } from 'google-maps';
import { MachineMarkerOptions } from '@/flightpath-components/models/MachineMarkerOptions';
import { getModule } from 'vuex-module-decorators';
import MachineStore from '@/store/modules/machine';
import MissionStore from '@/store/modules/missions';
import { MachineMarker } from '@/flightpath-components/models/mqttPayload/MachineMarkerMessage';
import { MachineKey } from '@/flightpath-components/models/MachineKey';
import MenuStore from '@/store/modules/menu';
import { Icons } from '@/models/Icons';

@Component
export default class GoogleMapMachineMarker extends Vue {
  @Prop() googleApi!: google;
  @Prop() map!: google.maps.Map<HTMLDivElement>;
  @Prop() marker!: MachineMarkerOptions;
  @Prop() updateMessage!: MachineMarker;
  @Prop() showSelectedMachine!: boolean;
  @Prop() showInfoWindow!: boolean;
  @Prop() allowDuplicateMarkerPositions!: boolean;
  @Prop() followMachine!: boolean;

  mapMarker = null as google.maps.Marker | null;
  mapCircle = null as google.maps.Marker | null;
  resumePoint = null as google.maps.Marker | null;
  public machineStore = getModule(MachineStore, this.$store);
  public missionStore = getModule(MissionStore, this.$store);
  public menuStore = getModule(MenuStore, this.$store);
  infoListenerHandle: google.maps.MapsEventListener | null = null;
  selectedListenerHandle: google.maps.MapsEventListener | null = null;
  infowindow: google.maps.InfoWindow | null = null;

  get markerAnchor() {
    return new this.googleApi.maps.Point(24, 24);
  }
  @Watch('marker')
  onMarkerChange(val: MachineMarkerOptions, oldVal: MachineMarkerOptions) {
    const valPos = val.MarkerOptions.position as google.maps.LatLng;
    const oldValPos = oldVal.MarkerOptions.position as google.maps.LatLng;

    if (this.missionStore.deployedMission?.TargetMachine?.machineId == val.Machine.machineId && val.UpdateMessage.RosState <= 1) {
      if (this.resumePointPosition != null && this.resumePoint != null) {
        this.resumePoint.setMap(this.map);
        this.resumePoint.setPosition(this.resumePointPosition);

        if (this.resumePoint != null)
          this.resumePoint.setIcon({
            url: this.CreateResumeMarker(Icons.ResumePoint),
            anchor: this.markerAnchor
          });
      } else {
        this.resumePoint?.setMap(null);
      }
    } else {
      this.resumePoint?.setMap(null);
    }

    if (
      this.mapMarker &&
      (val.MarkerOptions.zIndex != oldVal.MarkerOptions.zIndex ||
        ((valPos.lat != oldValPos.lat || valPos.lng != oldValPos.lng || this.allowDuplicateMarkerPositions) &&
          Date.parse(val.UpdateMessage.SentUTC) > Date.parse(oldVal.UpdateMessage.SentUTC)))
    ) {
      if (this.mapMarker != null) {
        this.mapMarker.setPosition(this.marker.MarkerOptions.position as google.maps.LatLng);
        this.mapMarker.setVisible(true);
        if (val.MarkerOptions.zIndex != null) {
          this.mapMarker.setZIndex(val.MarkerOptions.zIndex as number);
        }
      }

      if (this.mapMarker != null)
        this.mapMarker.setIcon({
          url: this.CreateSvgMarker(this.marker.UpdateMessage.Icon),
          anchor: this.markerAnchor
        });

      if (this.infowindow != null && this.showInfoWindow) this.infowindow.setContent(this.GetInfoContent());
      if (
        this.showSelectedMachine &&
        this.mapMarker != null &&
        this.machineStore.selectedMachine != null &&
        this.machineStore.selectedMachine.machineId == val.UpdateMessage.Key.machineId
      ) {
        this.mapCircle?.setPosition(this.mapMarker.getPosition() as google.maps.LatLng);
        this.machineStore.setSelectedMachineMarker(val.UpdateMessage);
        if (this.followMachine) {
          this.map.setCenter(this.mapMarker.getPosition() as google.maps.LatLng);
        }
      } else if (
        this.mapMarker != null &&
        this.machineStore.selectedMachine != null &&
        this.machineStore.selectedMachine.machineId == val.UpdateMessage.Key.machineId &&
        this.followMachine
      ) {
        this.map.setCenter(this.mapMarker.getPosition() as google.maps.LatLng);
      }
    }
  }
  private getDataURI(iconBase64: string) {
    return 'data:image/svg+xml;base64, ' + iconBase64;
  }

  @Watch('machineStore.selectedMachine')
  onSelectedMachineChange(value: MachineKey): void {
    if (value == null) {
      if (this.mapCircle != null) {
        this.mapCircle.setMap(null);
      }
    } else {
      if (
        this.showSelectedMachine &&
        this.mapMarker != null &&
        this.machineStore.selectedMachine != null &&
        value != null &&
        value.machineId == this.updateMessage.Key.machineId
      ) {
        this.machineStore.setSelectedMachineMarker(this.marker.UpdateMessage);
        this.mapMarker.setZIndex(99);
        const circleSymbol: google.maps.Symbol = this.CalculateCircleSymbol();
        if (this.mapCircle != null) {
          this.mapCircle.setIcon(circleSymbol);
          this.mapCircle.setPosition(this.mapMarker.getPosition() as google.maps.LatLng);
          this.mapCircle.setZIndex(50);
          this.mapCircle.setMap(this.map);
        } else {
          this.mapCircle = new this.googleApi.maps.Marker({
            icon: circleSymbol,
            position: this.mapMarker.getPosition() as google.maps.LatLng,
            map: this.map,
            zIndex: 50
          });
        }

        const position = {
          lat: this.updateMessage.Location.Location.Latitude,
          lng: this.updateMessage.Location.Location.Longitude
        };
        this.map.setCenter(position as unknown as google.maps.LatLng);
      } else {
        if (this.mapCircle != null) {
          this.mapCircle.setMap(null);
        }
        if (this.mapMarker != null) this.mapMarker.setZIndex(1);
      }
    }
  }

  // Google Maps throws on a NaN coordinate, so a missing or partial resume point gives no position
  get resumePointPosition(): google.maps.LatLngLiteral | null {
    const point = this.missionProgress?.resume_point;
    if (point == null || !Number.isFinite(point.latitude) || !Number.isFinite(point.longitude)) return null;
    return { lat: point.latitude, lng: point.longitude };
  }

  get missionProgress() {
    let missionProgress = this.missionStore.currentMissionProgress;
    let machineProgress = this.machineStore.missionProgress;

    if (machineProgress === null || machineProgress.waypoint_index === 0) {
      return missionProgress;
    } else return machineProgress;
  }

  private AddInfoListner() {
    if (this.infoListenerHandle != null) {
      this.infoListenerHandle.remove();
    }
    if (this.mapMarker != null && this.showInfoWindow) {
      this.infoListenerHandle = this.mapMarker.addListener('click', () => {
        if (this.infowindow != null) this.infowindow.open(this.map, this.mapMarker as google.maps.MVCObject);
      });
    }
  }

  private AddSelectedListner() {
    if (this.selectedListenerHandle != null) {
      this.selectedListenerHandle.remove();
    }
    if (this.mapMarker != null) {
      this.selectedListenerHandle = this.mapMarker.addListener('click', () => {
        this.machineStore.setSelectedMachine(this.marker.Machine);

        this.menuStore.setupMachineMenuState();
      });
    }
  }

  destroyed() {
    this.destroyResources();
  }
  private destroyResources() {
    if (this.infoListenerHandle != null) {
      this.infoListenerHandle.remove();
    }
    if (this.selectedListenerHandle != null) {
      this.selectedListenerHandle.remove();
    }
    if (this.mapMarker) {
      this.mapMarker.setVisible(false);
      this.mapMarker.setMap(null);
    }
    if (this.mapCircle) {
      this.mapCircle.setVisible(false);
      this.mapCircle.setMap(null);
    }
    if (this.resumePoint) {
      this.resumePoint.setVisible(false);
      this.resumePoint.setMap(null);
    }
  }

  deactivated() {
    if (this.infoListenerHandle != null) {
      this.infoListenerHandle.remove();
    }
    if (this.selectedListenerHandle != null) {
      this.selectedListenerHandle.remove();
    }
  }

  private CalculateCircleSymbol(): google.maps.Symbol {
    return {
      path: this.googleApi.maps.SymbolPath.CIRCLE,
      strokeOpacity: 0.8,
      strokeWeight: 1.5,
      strokeColor: '#FFFFFF',
      fillColor: '#FFFFFF',
      fillOpacity: 0.5,
      scale: 30.0
    };
  }

  private CreateSvgMarker(iconBase64: string): string {
    const svgstr = atob(iconBase64);
    const parser = new DOMParser();
    const svg = parser.parseFromString(svgstr, 'image/svg+xml');
    const svgTag = svg.getElementsByTagName('svg')[0];

    // svg transform attribute not supported by safari, we must use an inner <g> element for our rotation to work on iOS
    // see https://stackoverflow.com/questions/48248512/svg-transform-rotate180-does-not-work-in-safari-11
    const innerGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    innerGroup.innerHTML = svgTag.innerHTML;

    // set height and width equal so icon is not clipped off when rotated
    svgTag.setAttribute('width', '48');
    svgTag.setAttribute('height', '48');
    // rotate image, scale down so icon is not clipped off at corners, translate icon to center it on gps point
    innerGroup.setAttribute('transform', `scale(0.9, 0.9) translate(1.6 2.4) rotate(${this.updateMessage.Location.Location.Bearing} 16 24)`);

    svgTag.innerHTML = '';
    svgTag.appendChild(innerGroup);

    const icon = btoa(new XMLSerializer().serializeToString(svg));

    return this.getDataURI(icon);
  }
  private CreateResumeMarker(iconBase64: string): string {
    if (this.missionProgress == null) return '';
    const svgstr = atob(iconBase64);
    const parser = new DOMParser();
    const svg = parser.parseFromString(svgstr, 'image/svg+xml');
    const svgTag = svg.getElementsByTagName('svg')[0];

    // svg transform attribute not supported by safari, we must use an inner <g> element for our rotation to work on iOS
    // see https://stackoverflow.com/questions/48248512/svg-transform-rotate180-does-not-work-in-safari-11
    const innerGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    innerGroup.innerHTML = svgTag.innerHTML;

    // set height and width equal so icon is not clipped off when rotated
    svgTag.setAttribute('width', '48');
    svgTag.setAttribute('height', '48');
    // rotate image, scale down so icon is not clipped off at corners, translate icon to center it on gps point
    innerGroup.setAttribute('transform', `scale(0.9, 0.9) translate(1.6 2.4) rotate(${this.missionProgress?.resume_bearing} 16 24)`);

    svgTag.innerHTML = '';
    svgTag.appendChild(innerGroup);

    const icon = btoa(new XMLSerializer().serializeToString(svg));

    return this.getDataURI(icon);
  }
  mounted() {
    this.machineStore = getModule(MachineStore, this.$store);
    this.missionStore = getModule(MissionStore, this.$store);
    this.menuStore = getModule(MenuStore, this.$store);
    const circleSymbol: google.maps.Symbol = this.CalculateCircleSymbol();
    this.infowindow = this.GenerateInfoWindow();
    this.mapMarker = new this.googleApi.maps.Marker({
      position: this.marker.MarkerOptions.position,
      clickable: true,
      map: this.map,
      icon: {
        url: this.CreateSvgMarker(this.marker.UpdateMessage.Icon),
        anchor: this.markerAnchor
      },
      title: this.marker.MarkerOptions.title,
      zIndex: this.marker.MarkerOptions.zIndex ?? 1
    });
    this.resumePoint = new this.googleApi.maps.Marker({
      position: this.resumePointPosition ?? undefined,
      clickable: false,
      map: this.map,
      icon: {
        url: this.CreateResumeMarker(Icons.ResumePoint),
        anchor: this.markerAnchor
      },
      title: this.marker.MarkerOptions.title,
      zIndex: this.marker.MarkerOptions.zIndex ?? 1
    });
    this.resumePoint.setMap(null);

    this.AddInfoListner();
    this.AddSelectedListner();

    if (
      this.showSelectedMachine &&
      this.machineStore.selectedMachine != null &&
      this.machineStore.selectedMachine.machineId == this.updateMessage.Key.machineId
    ) {
      this.mapCircle = new this.googleApi.maps.Marker({
        icon: circleSymbol,
        position: this.marker.MarkerOptions.position,
        map: this.map,
        zIndex: 50
      });
      this.map.setCenter(this.marker.MarkerOptions.position as google.maps.LatLng);
    }

    console.info('Machine Added', this.marker.Machine.machineId);
  }

  private GenerateInfoWindow() {
    if (!this.showInfoWindow) return null;
    const contentString = this.GetInfoContent();

    const infowindow = new this.googleApi.maps.InfoWindow({
      content: contentString
    });
    return infowindow;
  }

  private GetInfoContent() {
    return `<div id="content">
      <h1 id="firstHeading" class="firstHeading">Machine Status</h1>
      <div id="bodyContent">
      <p><b>Machine: ${this.updateMessage.Key.MachineName ?? this.updateMessage.Key.machineId}</b></p>
      <p><b>lattitude: ${this.updateMessage.Location.Location.Latitude}</b></p>
      <p><b>Longitude: ${this.updateMessage.Location.Location.Longitude}</b></p>
      <p><b>Bearing: ${Math.abs(this.updateMessage.Location.Location.Bearing).toFixed(4)}</b></p>
      <p><b>Speed: ${this.updateMessage.Location.Location.Speed.toFixed(2)}</b></p>
      </div>
      </div>`;
  }
}
